<template>
  <div class="cbhi-card-container">
    <!-- Card Preview -->
    <div class="card-preview-wrapper" ref="cardPreview">
      <div class="cbhi-card" :class="{ 'card-back': showBack }">
        <!-- Front of Card -->
        <div class="card-front" v-show="!showBack">
          <!-- Header Section -->
          <div class="card-header">
            <div class="header-left">
              <img :src="'/logos/moh_logo.png'" alt="MOH Logo" class="moh-logo">
            </div>
            <div class="header-center">
              <h5 class="card-title">Federal Democratic Republic of Ethiopia</h5>
              <h6 class="card-subtitle">Community-Based Health Insurance</h6>
              <p class="card-subtitle-amharic">የማህበረሰብ ጤና ኢንሹራንስ</p>
            </div>
            <div class="header-right">
              <img :src="'/logos/ethiopia_logo.svg'" alt="CBHI Logo" class="cbhi-logo">
            </div>
          </div>

          <!-- Member Photo and Info Section -->
          <div class="card-body">
            <div class="photo-section">
              <div class="member-photo-wrapper">
                <img v-if="hasProfilePhoto"
                     :src="getMemberPhotoUrl()"
                     alt="Member Photo"
                     class="member-photo"
                     @error="onPhotoError">
                <div v-else class="member-photo-placeholder">
                  <i class="bx bx-user"></i>
                </div>
              </div>
              <div class="qr-code-wrapper">
                <canvas ref="qrCanvas" class="qr-code"></canvas>
              </div>
            </div>

            <div class="info-section">
              <div class="member-info-grid">
                <div class="info-row">
                  <span class="info-label">Card Number:</span>
                  <span class="info-value card-number">{{ memberInfo.cbhiNumber || memberInfo.cbhi_number }}</span>
                </div>

                <div class="info-row">
                  <span class="info-label">Full Name:</span>
                  <span class="info-value member-name">{{ memberInfo.fullName || memberInfo.full_name }}</span>
                </div>

                <div class="info-row">
                  <span class="info-label">Date of Birth:</span>
                  <span class="info-value">{{ formatDate(memberInfo.dateOfBirth || memberInfo.date_of_birth) }}</span>
                </div>

                <div class="info-row">
                  <span class="info-label">Gender:</span>
                  <span class="info-value">{{ capitalizeFirst(memberInfo.gender) }}</span>
                </div>

                <div class="info-row">
                  <span class="info-label">Plan:</span>
                  <span class="info-value plan-name">{{ planInfo.plan_name }}</span>
                </div>

                <div class="info-row">
                  <span class="info-label">Valid Until:</span>
                  <span class="info-value expiry-date">{{ formatDate(memberInfo.expiryDate || memberInfo.expiry_date) }}</span>
                </div>

                <div class="info-row">
                  <span class="info-label">Region:</span>
                  <span class="info-value">{{ memberInfo.regionName || memberInfo.region_name }}</span>
                </div>

                <div class="info-row">
                  <span class="info-label">Woreda:</span>
                  <span class="info-value">{{ memberInfo.woredaName || memberInfo.woreda_name }}</span>
                </div>
              </div>

              <!-- Coverage Badge -->
              <div class="coverage-badge">
                <i class="bx bx-shield-check"></i>
                <span>Coverage: ETB {{ formatCurrency(planInfo.annual_coverage_limit) }}</span>
              </div>
            </div>
          </div>

          <!-- Card Footer -->
          <div class="card-footer">
            <div class="footer-left">
              <p class="issue-date">Issued: {{ formatDate(issueDate) }}</p>
              <p class="card-note">This card is property of MOH Ethiopia</p>
            </div>
            <div class="footer-right">
              <p class="emergency-number">
                <i class="bx bx-phone"></i>
                Emergency: 952 / 907
              </p>
            </div>
          </div>
        </div>

        <!-- Back of Card -->
        <div class="card-back-side" v-show="showBack">
          <div class="back-header">
            <div class="back-header-logos">
              <img src="/logos/moh-fdre-color.png" alt="MOH" class="back-logo">
            </div>
            <h6 class="text-white">Important Information</h6>
            <p class="amharic-text">አስፈላጊ መረጃ</p>
          </div>

          <div class="back-content">
            <div class="instructions-section">
              <h6><i class="bx bx-info-circle"></i> How to Use This Card:</h6>
              <ul class="instruction-list">
                <li>Present this card at any contracted health facility</li>
                <li>Services are subject to plan coverage limits</li>
                <li>Keep this card safe and report if lost</li>
                <li>Valid only with photo ID verification</li>
              </ul>
            </div>

            <div class="covered-members-section" v-if="beneficiaryCount > 0">
              <h6><i class="bx bx-group"></i> Covered Members:</h6>
              <p class="covered-count">{{ totalMembers }} members covered under this plan</p>
              <p class="members-note">Primary Member + {{ beneficiaryCount }} Beneficiaries</p>
            </div>

            <div class="emergency-section">
              <h6><i class="bx bx-first-aid"></i> Emergency Contact:</h6>
              <p>CBHI Hotline: <strong>952</strong></p>
              <p>Health Emergency: <strong>907</strong></p>
              <p>Email: info@cbhi.moh.gov.et</p>
            </div>

            <div class="signature-section">
              <div class="signature-line">
                <span class="signature-label">Authorized Signature</span>
              </div>
              <p class="card-validity">This card remains property of Federal Ministry of Health</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Card Actions -->
    <div class="card-actions" v-if="!printMode">
      <button class="btn btn-info btn-sm" @click="toggleCardSide">
        <i class="bx bx-refresh"></i>
        {{ showBack ? 'Show Front' : 'Show Back' }}
      </button>
      <button class="btn btn-primary btn-sm" @click="printCard">
        <i class="bx bx-printer"></i>
        Print Card
      </button>
      <button class="btn btn-success btn-sm" @click="downloadCard">
        <i class="bx bx-download"></i>
        Download as PDF
      </button>
      <button v-if="!isCardIssued" class="btn btn-warning btn-sm" @click="markAsIssued">
        <i class="bx bx-check-circle"></i>
        Mark as Issued
      </button>
    </div>
  </div>
</template>

<script>
import QRCode from 'qrcode';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export default {
  name: 'CBHICard',
  props: {
    memberInfo: {
      type: Object,
      required: true
    },
    planInfo: {
      type: Object,
      required: true
    },
    beneficiaryCount: {
      type: Number,
      default: 0
    },
    issueDate: {
      type: String,
      default: () => new Date().toISOString().split('T')[0]
    },
    printMode: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      showBack: false
    };
  },

  computed: {
    hasProfilePhoto() {
      return this.memberInfo.profile_photo || this.memberInfo.profilePhoto;
    },

    isCardIssued() {
      return this.memberInfo.cbhiCardIssued === 1 ||
          this.memberInfo.cbhiCardIssued === '1' ||
          this.memberInfo.cbhi_card_issued === 1;
    },

    totalMembers() {
      return 1 + this.beneficiaryCount;
    }
  },

  mounted() {
    this.generateQRCode();
  },

  methods: {
    getMemberPhotoUrl() {
      const photo = this.memberInfo.profile_photo || this.memberInfo.profilePhoto;
      if (!photo) return '';

      if (photo.startsWith('http')) {
        return photo;
      }
      return `https://rcmsuat.craftsilicon.com/api1/uploads/member-photos/${photo}`;
    },

    onPhotoError(event) {
      event.target.style.display = 'none';
      const placeholder = event.target.nextElementSibling;
      if (placeholder) {
        placeholder.style.display = 'flex';
      }
    },

    async generateQRCode() {
      const qrData = JSON.stringify({
        cbhiNumber: this.memberInfo.cbhiNumber || this.memberInfo.cbhi_number,
        fullName: this.memberInfo.fullName || this.memberInfo.full_name,
        dob: this.memberInfo.dateOfBirth || this.memberInfo.date_of_birth,
        plan: this.planInfo.plan_code,
        expiry: this.memberInfo.expiryDate || this.memberInfo.expiry_date
      });

      try {
        const canvas = this.$refs.qrCanvas;
        if (canvas) {
          await QRCode.toCanvas(canvas, qrData, {
            width: 120,
            margin: 1,
            color: {
              dark: '#000000',
              light: '#FFFFFF'
            }
          });
        }
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    },

    toggleCardSide() {
      this.showBack = !this.showBack;
    },

    async printCard() {
      const originalBack = this.showBack;

      // Print front
      this.showBack = false;
      await this.$nextTick();
      await new Promise(resolve => setTimeout(resolve, 100));
      window.print();

      // Wait and print back
      await new Promise(resolve => setTimeout(resolve, 1000));
      this.showBack = true;
      await this.$nextTick();
      await new Promise(resolve => setTimeout(resolve, 100));
      window.print();

      this.showBack = originalBack;
    },

    async downloadCard() {
      try {
        const cardEl = this.$refs.cardElement || this.$refs.cardPreview;
        if (!cardEl) {
          alert('Card element not found.');
          return;
        }

        // ---- FRONT SIDE ----
        this.showBack = false;
        await this.$nextTick();
        await new Promise(resolve => setTimeout(resolve, 200));

        const frontCanvas = await html2canvas(cardEl, {
          scale: 3,           // high quality
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff'
        });

        // Keep aspect ratio from the canvas itself
        const canvasWidthPx  = frontCanvas.width;
        const canvasHeightPx = frontCanvas.height;
        const aspectRatio    = canvasWidthPx / canvasHeightPx;

        // Choose desired physical width in mm (credit card width)
        const cardWidthMm  = 85.6;
        const cardHeightMm = cardWidthMm / aspectRatio; // computed from canvas → no distortion

        // Create PDF page with matching aspect ratio
        const pdf = new jsPDF({
          orientation: cardWidthMm > cardHeightMm ? 'landscape' : 'portrait',
          unit: 'mm',
          format: [cardWidthMm, cardHeightMm]
        });

        const frontImg = frontCanvas.toDataURL('image/png', 1.0);
        pdf.addImage(frontImg, 'PNG', 0, 0, cardWidthMm, cardHeightMm, '', 'FAST');

        // ---- BACK SIDE ----
        this.showBack = true;
        await this.$nextTick();
        await new Promise(resolve => setTimeout(resolve, 200));

        const backCanvas = await html2canvas(cardEl, {
          scale: 3,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff'
        });

        const backImg = backCanvas.toDataURL('image/png', 1.0);
        pdf.addPage([cardWidthMm, cardHeightMm], cardWidthMm > cardHeightMm ? 'landscape' : 'portrait');
        pdf.addImage(backImg, 'PNG', 0, 0, cardWidthMm, cardHeightMm, '', 'FAST');

        // Reset to front
        this.showBack = false;
        await this.$nextTick();

        const fileName = `CBHI_Card_${this.memberInfo.cbhiNumber || this.memberInfo.cbhi_number}.pdf`;
        pdf.save(fileName);
      } catch (error) {
        console.error('Error generating PDF:', error);
        alert('Failed to generate PDF. Please try again.');
      }
    },


    async markAsIssued() {
      this.$emit('mark-as-issued');
    },

    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat().format(amount || 0);
    },

    capitalizeFirst(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    }
  }
};
</script>

<style scoped>
/* Card Container */
.cbhi-card-container {
  max-width: 100%;
  margin: 0 auto;
  padding: 20px;
}

.card-preview-wrapper {
  perspective: 1000px;
  margin-bottom: 20px;
}

/* Card Base Styles - Credit Card Dimensions */
.cbhi-card {
  width: 856px; /* 85.6mm * 10 */
  height: 540px; /* 53.98mm * 10 */
  margin: 0 auto;
  border-radius: 15px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  background: transparent;
  position: relative;
  transition: transform 0.6s;
  transform-style: preserve-3d;
}

/* Card Front */
.card-front {
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 2px solid #e0e0e0;
  border-radius: 15px;
  display: flex;
  flex-direction: column;
  z-index: 2;
}

/* Card Back */
.card-back-side {
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 2px solid #e0e0e0;
  border-radius: 15px;
  transform: rotateY(180deg);
  padding: 20px;
  z-index: 1;
  display: flex;
  flex-direction: column;
}

.cbhi-card.card-back {
  transform: rotateY(180deg);
}

/* Header Section */
.card-header {
  background: linear-gradient(135deg, #2c5aa0 0%, #4a90e2 100%);
  padding: 15px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 15px 15px 0 0;
  color: white;
}

.header-left, .header-right {
  flex: 0 0 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-center {
  flex: 1;
  text-align: center;
}

.moh-logo, .cbhi-logo {
  height: 60px;
  width: auto;
  max-width: 75px;
  object-fit: contain;
  background: white;
  padding: 8px;
  border-radius: 8px;
}

.card-title {
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  color: white;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.card-subtitle {
  font-size: 14px;
  font-weight: 600;
  margin: 2px 0;
  color: rgba(255, 255, 255, 0.95);
}

.card-subtitle-amharic {
  font-size: 12px;
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
  font-family: 'Noto Sans Ethiopic', sans-serif;
}

/* Body Section */
.card-body {
  flex: 1;
  display: flex;
  padding: 20px;
  gap: 20px;
}

.photo-section {
  flex: 0 0 180px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.member-photo-wrapper {
  width: 140px;
  height: 170px;
  border: 3px solid #2c5aa0;
  border-radius: 10px;
  overflow: hidden;
  background: white;
  margin: 0 auto;
}

.member-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.member-photo-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  color: #2c5aa0;
  font-size: 60px;
}

.qr-code-wrapper {
  width: 120px;
  height: 120px;
  margin: 0 auto;
  padding: 5px;
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
}

.qr-code {
  width: 100% !important;
  height: 100% !important;
}

/* Info Section */
.info-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.member-info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  flex: 1;
}

.info-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.info-label {
  font-size: 11px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.card-number {
  color: #2c5aa0;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 1px;
}

.member-name {
  color: #000;
  font-size: 16px;
  font-weight: 700;
  text-transform: uppercase;
}

.plan-name {
  color: #4a90e2;
  font-weight: 700;
}

.expiry-date {
  color: #dc3545;
  font-weight: 700;
}

/* Coverage Badge */
.coverage-badge {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  padding: 12px 15px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
}

.coverage-badge i {
  font-size: 24px;
}

/* Footer Section */
.card-footer {
  background: #f8f9fa;
  padding: 12px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 0 0 15px 15px;
  border-top: 2px solid #e0e0e0;
}

.footer-left, .footer-right {
  font-size: 10px;
}

.issue-date {
  font-weight: 600;
  color: #333;
  margin: 0;
}

.card-note {
  color: #666;
  margin: 2px 0 0 0;
  font-style: italic;
}

.emergency-number {
  font-weight: 700;
  color: #dc3545;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 5px;
}

/* Card Back Styles */
.back-header {
  background: linear-gradient(135deg, #2c5aa0 0%, #4a90e2 100%);
  color: white;
  padding: 15px;
  border-radius: 10px;
  margin-bottom: 15px;
  text-align: center;
  position: relative;
}

.back-header-logos {
  position: absolute;
  top: 10px;
  right: 15px;
}

.back-logo {
  height: 35px;
  width: auto;
  background: white;
  padding: 4px;
  border-radius: 5px;
}

.back-header h6 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
}

.amharic-text {
  margin: 5px 0 0 0;
  font-size: 14px;
  font-family: 'Noto Sans Ethiopic', sans-serif;
}

.back-content {
  display: flex;
  flex-direction: column;
  gap: 15px;
  flex: 1;
}

.instructions-section,
.covered-members-section,
.emergency-section {
  background: white;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.instructions-section h6,
.covered-members-section h6,
.emergency-section h6 {
  font-size: 13px;
  font-weight: 700;
  color: #2c5aa0;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 5px;
}

.instruction-list {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 11px;
  color: #333;
}

.instruction-list li {
  padding: 4px 0 4px 20px;
  position: relative;
}

.instruction-list li:before {
  content: "✓";
  position: absolute;
  left: 0;
  color: #28a745;
  font-weight: 700;
}

.covered-count {
  font-size: 13px;
  font-weight: 700;
  color: #333;
  margin: 5px 0;
}

.members-note {
  font-size: 11px;
  color: #666;
  margin: 0;
}

.emergency-section p {
  font-size: 12px;
  margin: 5px 0;
  color: #333;
}

.signature-section {
  margin-top: auto;
  padding-top: 15px;
}

.signature-line {
  border-top: 2px solid #333;
  padding-top: 5px;
  margin-bottom: 5px;
}

.signature-label {
  font-size: 10px;
  color: #666;
  font-style: italic;
}

.card-validity {
  font-size: 9px;
  color: #999;
  text-align: center;
  margin: 10px 0 0 0;
  font-style: italic;
}

/* Card Actions */
.card-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
}

.card-actions .btn {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Print Styles */
@media print {
  body * {
    visibility: hidden;
  }

  .cbhi-card-container,
  .cbhi-card-container * {
    visibility: visible;
  }

  .cbhi-card-container {
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    padding: 0;
  }

  .cbhi-card {
    width: 85.6mm;
    height: 53.98mm;
    box-shadow: none;
    page-break-after: always;
  }

  .card-actions {
    display: none !important;
  }
}

/* Responsive Design */
@media (max-width: 920px) {
  .cbhi-card {
    width: 100%;
    height: auto;
    aspect-ratio: 1.586;
  }

  .card-header {
    padding: 10px 15px;
  }

  .card-title {
    font-size: 14px;
  }

  .card-subtitle {
    font-size: 12px;
  }

  .moh-logo, .cbhi-logo {
    height: 45px;
  }

  .card-body {
    padding: 15px;
    gap: 15px;
  }

  .photo-section {
    flex: 0 0 140px;
  }

  .member-photo-wrapper {
    width: 110px;
    height: 135px;
  }

  .qr-code-wrapper {
    width: 100px;
    height: 100px;
  }

  .member-info-grid {
    gap: 8px;
  }

  .info-label {
    font-size: 10px;
  }

  .info-value {
    font-size: 12px;
  }

  .coverage-badge {
    padding: 10px 12px;
    font-size: 12px;
  }
}

@media (max-width: 600px) {
  .card-body {
    flex-direction: column;
  }

  .photo-section {
    flex-direction: row;
    justify-content: space-around;
    align-items: center;
  }

  .member-info-grid {
    grid-template-columns: 1fr;
  }
}
</style>